import { useTranslation } from "react-i18next";
import { TbArrowLeft, TbArrowRight, TbExclamationMark, TbSend } from "react-icons/tb";
import type { CreateWizard } from "./useCreateWizard";

const fmt = (n: number) =>
    n.toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Sticky bar at the bottom of the creation page: back, the running total (always visible, so
 * the price is never out of sight), and forward — or, on the last step, the create action.
 */
export function CreateFooterBar({
    wizard,
    total,
    canContinue,
    canCreate,
    creating = false,
    onCreate,
    sendLabel,
    onCreateAndSend,
    warningCount = 0,
    onShowWarnings,
}: {
    wizard: CreateWizard;
    total: number;
    canContinue: boolean;
    canCreate: boolean;
    /** Which create is running — shows the busy text on that button. */
    creating?: false | "create" | "send";
    onCreate: () => void;
    /** Story 12: *Erstellen & senden* (or "…, Versand nach Einwilligung") — then *Erstellen* steps back to a
     *  secondary button. Absent when the invoice can't be sent from here. */
    sendLabel?: string;
    onCreateAndSend?: () => void;
    /** Warnings on this step that can sit out of view above a long list. They never hold up *Weiter*. */
    warningCount?: number;
    onShowWarnings?: () => void;
}) {
    const { t } = useTranslation();

    return (
        // data-bottom-bar: dropdowns open upward instead of disappearing behind this bar (useDropDirection)
        <div className="ic-footer" data-bottom-bar>
            <div className="ic-footer__inner">
                <button type="button" className="ic-footer__back" onClick={wizard.back} disabled={wizard.isFirst}>
                    <TbArrowLeft /> {t("invoiceCreate.back")}
                </button>
                <div className="ic-footer__total">
                    <span className="ic-footer__total-label">{t("invoiceCreate.total")}</span>
                    <span className="ic-footer__total-value" data-testid="invoice-create-total">{fmt(total)}</span>
                </div>
                {wizard.isLast && sendLabel && onCreateAndSend ? (
                    <div className="ic-footer__create">
                        <button type="button" className="ic-footer__secondary" onClick={onCreate} disabled={!canCreate} aria-busy={creating === "create"}>
                            {creating === "create" ? t("invoiceCreate.creating") : t("invoiceCreate.create")}
                        </button>
                        <button type="button" className="ic-footer__primary" onClick={onCreateAndSend} disabled={!canCreate} aria-busy={creating === "send"}>
                            {creating === "send" ? t("invoiceCreate.creatingAndSending") : <><TbSend aria-hidden /> {sendLabel}</>}
                        </button>
                    </div>
                ) : wizard.isLast ? (
                    <button type="button" className="ic-footer__primary" onClick={onCreate} disabled={!canCreate} aria-busy={creating === "create"}>
                        {creating ? t("invoiceCreate.creating") : t("invoiceCreate.create")}
                    </button>
                ) : (
                    <div className="ic-footer__next">
                        <button type="button" className="ic-footer__primary" onClick={wizard.next} disabled={!canContinue}>
                            {t("invoiceCreate.next")} <TbArrowRight />
                        </button>
                        {warningCount > 0 && (
                            <button
                                type="button"
                                className="ic-footer__warning"
                                onClick={onShowWarnings}
                                aria-label={t("invoiceCreate.footerWarnings", { count: warningCount })}
                            >
                                <TbExclamationMark aria-hidden />
                                <span className="ic-footer__warning-tip" aria-hidden>{t("invoiceCreate.footerWarnings", { count: warningCount })}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
